/** Read-only derived views + small formatters for the UI. */
import type { City, Building, Colonist, Room, Activity } from './types';
import { SIM, roomDef, SKILL_META } from './config';
import { topSkill, isAvailable } from './colonist';
import { workersOf } from './jobs';
import { totalDefense, safeThreshold } from './heat';
import { roomAt, activeBuilding } from './world';

export function num(v: number): string {
  const n = Math.floor(v);
  if (Math.abs(n) < 1000) return n.toString();
  if (Math.abs(n) < 1_000_000) return (n / 1000).toFixed(n < 10_000 ? 1 : 0) + 'K';
  if (Math.abs(n) < 1_000_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  return (n / 1_000_000_000).toFixed(2) + 'B';
}
export function money(v: number): string {
  return '$' + num(v);
}

export function moodBand(mood: number): { label: string; color: string } {
  if (mood >= 65) return { label: 'content', color: 'var(--good)' };
  if (mood >= 40) return { label: 'edgy', color: 'var(--heat-warm)' };
  if (mood >= SIM.BREAK_THRESHOLD) return { label: 'stressed', color: 'var(--heat-hot)' };
  return { label: 'breaking', color: 'var(--bad)' };
}

export function needColor(v: number): string {
  if (v >= 50) return 'var(--good)';
  if (v >= 25) return 'var(--heat-warm)';
  return 'var(--bad)';
}

const ACTIVITY_LABEL: Record<Activity, string> = {
  idle: 'idle',
  goto: 'on the move',
  work: 'working',
  sleep: 'sleeping',
  eat: 'eating',
  relax: 'relaxing',
  flee: 'fleeing',
  recover: 'recovering',
  arrested: 'in jail',
};
export function activityLabel(c: Colonist): string {
  return ACTIVITY_LABEL[c.activity];
}
export function roleLabel(c: Colonist): string {
  return SKILL_META[topSkill(c)].name;
}

export interface RoomView {
  workers: Colonist[];
  capacity: number;
  damaged: boolean;
  damageLeft: number;
  assignable: boolean;
}
export function roomView(b: Building, room: Room, now: number): RoomView {
  const def = roomDef(room.type);
  return {
    workers: workersOf(b, room),
    capacity: def.workSlots,
    damaged: room.damagedUntil > now,
    damageLeft: Math.max(0, room.damagedUntil - now),
    assignable: !!def.workplace,
  };
}

export function staff(b: Building, roomId: number): Colonist[] {
  return b.colonists.filter((c) => c.assignedRoomId === roomId);
}

export interface Alert {
  kind: 'warn' | 'bad';
  text: string;
  /** present when clicking the alert should jump to a specific building */
  jumpTo?: number;
}
export function alerts(city: City): Alert[] {
  const out: Alert[] = [];
  const b = activeBuilding(city);
  if (!b) return out;
  const idle = b.colonists.filter((c) => c.assignedRoomId == null && isAvailable(c, city.t));
  if (idle.length) out.push({ kind: 'warn', text: `${idle.length} unassigned in ${b.name}` });
  if (!b.rooms.some((r) => r.type === 'bunk')) out.push({ kind: 'bad', text: 'No bunks — crew can’t rest' });
  if (!b.rooms.some((r) => r.type === 'diner')) out.push({ kind: 'bad', text: 'No diner — crew can’t eat' });
  if (b.res.meals < 3 && b.rooms.some((r) => r.type === 'diner')) out.push({ kind: 'warn', text: 'Larder low — assign a cook' });
  const hungry = b.colonists.filter((c) => c.needs.food < SIM.CRITICAL).length;
  if (hungry) out.push({ kind: 'bad', text: `${hungry} starving in ${b.name}` });
  const lowMood = b.colonists.filter((c) => c.mood < SIM.BREAK_THRESHOLD + 6).length;
  if (lowMood) out.push({ kind: 'warn', text: `${lowMood} near breaking point` });
  // any building under raid
  const raided = city.buildings.find((x) => x.raid);
  if (raided && raided.id !== city.activeId) out.push({ kind: 'bad', text: `🚨 Raid at ${raided.name} — tap to jump in`, jumpTo: raided.id });
  return out;
}

export function heatBand(heat: number): 'cool' | 'warm' | 'hot' | 'critical' {
  if (heat < 38) return 'cool';
  if (heat < 62) return 'warm';
  if (heat < 84) return 'hot';
  return 'critical';
}

export function emptySlots(b: Building): Array<{ floor: number; slot: number }> {
  const out: Array<{ floor: number; slot: number }> = [];
  for (let f = b.floorMin; f <= b.floorMax; f++) {
    for (let s = 0; s < SIM.SLOTS_PER_FLOOR; s++) {
      if (!roomAt(b, f, s)) out.push({ floor: f, slot: s });
    }
  }
  return out;
}

export function defenseSummary(b: Building, now: number): { defense: number; safe: number } {
  return { defense: Math.round(totalDefense(b, now)), safe: safeThreshold(b) };
}
